import type { MutationTree } from 'vuex';

import { STEPS_NAMES } from '@consts/steps.const';
import type { DocumentType } from '@store/Steps/types';

import type { AddressData, AppError, ScannedDocData, SelectedCountry, StepsState } from './interfaces';

export enum StepsMutations {
  SetDataLoading = 'Steps:SET_DATA_LOADING',
  SetCurrentStep = 'Steps:SET_CURRENT_STEP',
  IncreaseCurrentStep = 'Steps:INCREASE_CURRENT_STEP',
  DecreaseCurrentStep = 'Steps:DECREASE_CURRENT_STEP',
  SetError = 'Steps:SET_ERROR',
  ClearError = 'Steps:CLEAR_ERROR',
  SetSelectedCountry = 'Steps:SET_SELECTED_COUNTRY',
  SetSelectedDocs = 'Steps:SET_SELECTED_DOCS',
  SetDisclaimerStatus = 'Steps:SET_DISCLAIMER_STATUS',
  SetApplicantId = 'Steps:SET_APPLICANT_ID',
  SetScannedDocData = 'Steps:SET_SCANNED_DOC_DATA',
  AddDocumentCheckId = 'Steps:ADD_DOCUMENT_CHECK_ID',
  SetAddressData = 'Steps:SET_ADDRESS_DATA',
  SetBookingDetails = 'Steps:SET_BOOKING_DETAILS',
  SetOnfidoToken = 'Steps:SET_ONFIDO_TOKEN',
}

export interface StepsData {
  [StepsMutations.SetDataLoading]: (state: StepsState, isLoading: boolean) => void;
  [StepsMutations.SetCurrentStep]: (state: StepsState, stepName: string) => void;
  [StepsMutations.IncreaseCurrentStep]: (state: StepsState) => void;
  [StepsMutations.DecreaseCurrentStep]: (state: StepsState) => void;
  [StepsMutations.SetError]: (state: StepsState, error: AppError) => void;
  [StepsMutations.ClearError]: (state: StepsState) => void;
  [StepsMutations.SetSelectedCountry]: (state: StepsState, selectedCountry: SelectedCountry) => void;
  [StepsMutations.SetSelectedDocs]: (state: StepsState, documents: string[]) => void;
  [StepsMutations.SetDisclaimerStatus]: (state: StepsState, isChecked: boolean) => void;
  [StepsMutations.SetApplicantId]: (state: StepsState, applicantId: string) => void;
  [StepsMutations.SetScannedDocData]: (state: StepsState, scannedDocData: ScannedDocData) => void;
  [StepsMutations.AddDocumentCheckId]: (state: StepsState, documentCheckId: string) => void;
  [StepsMutations.SetAddressData]: (state: StepsState, addressData: Partial<AddressData>) => void;
  [StepsMutations.SetBookingDetails]: (state: StepsState, bookingDetails: any) => void;
  [StepsMutations.SetOnfidoToken]: (state: StepsState, token: string) => void;
}

export const mutations: MutationTree<StepsState> & StepsData = {
  [StepsMutations.SetDataLoading](state, isLoading) {
    state.isDataLoading = isLoading;
  },

  [StepsMutations.SetCurrentStep](state, stepName) {
    state.current = stepName;
  },

  [StepsMutations.IncreaseCurrentStep](state) {
    const stepsKeys = Object.keys(STEPS_NAMES);
    const currentStepKey = stepsKeys.indexOf(state.current);
    state.current = stepsKeys[currentStepKey + 1];
  },

  [StepsMutations.DecreaseCurrentStep](state) {
    const stepsKeys = Object.keys(STEPS_NAMES);
    const nextStepKey = stepsKeys.indexOf(state.current) - 1;

    const isSelectCountryStep = state.current === STEPS_NAMES.selectCountry;
    const isSelectDocumentsStep = state.current === STEPS_NAMES.selectDocuments;
    const { isDisclaimerChecked } = state.steps[STEPS_NAMES.selectDocuments];

    if (isSelectCountryStep) {
      state.steps[STEPS_NAMES.validateBooking].bookingId = '';
      state.steps[STEPS_NAMES.validateBooking].email = '';
      state.steps[STEPS_NAMES.validateBooking].confirmEmail = '';
      state.steps[STEPS_NAMES.validateBooking].bookingDate = '';
    }

    if (isSelectDocumentsStep && isDisclaimerChecked) {
      state.steps[STEPS_NAMES.selectDocuments].isDisclaimerChecked = true;
    }

    state.current = stepsKeys[nextStepKey];
  },

  [StepsMutations.SetError](state, error) {
    state.error = error;
  },

  [StepsMutations.ClearError](state) {
    state.error.backend = '';
    state.error.translation = '';
  },

  [StepsMutations.SetSelectedCountry](state, { name, code }) {
    state.steps[STEPS_NAMES.selectCountry].selectedCountryName = name;
    state.steps[STEPS_NAMES.selectCountry].selectedCountryCode = code;
  },

  [StepsMutations.SetSelectedDocs](state, documents) {
    state.steps[STEPS_NAMES.selectDocuments].selectedDocuments = documents;
  },

  [StepsMutations.SetDisclaimerStatus](state, isChecked) {
    state.steps[STEPS_NAMES.selectDocuments].isDisclaimerChecked = isChecked;
  },

  [StepsMutations.SetApplicantId](state, applicantId) {
    state.steps[STEPS_NAMES.validateDocuments].applicantId = applicantId;
  },

  [StepsMutations.SetScannedDocData](state, scannedDocData) {
    state.steps[STEPS_NAMES.validateDocuments].documentType = scannedDocData.type as DocumentType;
    state.steps[STEPS_NAMES.validateDocuments].documents = scannedDocData.ids;
  },

  [StepsMutations.AddDocumentCheckId](state, documentCheckId) {
    state.steps[STEPS_NAMES.validateDocuments].documentsChecksIds.push(documentCheckId);
  },

  [StepsMutations.SetAddressData]({ steps }, addressData) {
    const currentAddress = steps[STEPS_NAMES.updateAddress];
    steps[STEPS_NAMES.updateAddress] = {
      ...currentAddress,
      ...addressData,
    };
  },
  [StepsMutations.SetBookingDetails](state, bookingDetails) {
    state.steps[STEPS_NAMES.validateBooking] = { ...bookingDetails };
  },
  [StepsMutations.SetOnfidoToken](state, token) {
    state.steps[STEPS_NAMES.validateDocuments].token = token;
  },
} as const;
